import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { pathname } from '../../../utils/path/path';
import { useLinesContext } from '../../../contexts/Lines';
import { useSnackbarContext } from '../../../contexts/Snackbar';
import { deleteAllFiles } from '../../uploadDocument/UploadCard';
import messages from '../../../messages';
import moment from 'moment';
import TypeOfChanges from './TypeOfChanges';
import EditStagesConfirmation from './EditStagesConfirmation';
import CancelLineConfirmation from './CancelLineConfirmation';
import PaymentRedirect from './PaymentRedirect';
import AddMessage from './AddMessage';
import SubmitRequest from './SubmitRequest';
import AdjustMyRemittanceSchedule from './AdjustMyRemittanceSchedule';
import SomethingElse from './SomethingElse';

const initialState = {
  LineDateChanged: false,
  LineDetailsChanged: false,
  messageAdded: '',
  messageOnly: false,
  adjustMyRemittanceScheduleOnly: false,
  cancelLine: false,
  files: null,
};

export enum actions {
  None = 0,
  editStages,
  changePayment,
  addMessage,
  cancelLine,
  cancel,
  adjustMyRemittanceSchedule,
  uploadFileSubmit,
}

export enum steps {
  chooseOption = 0,
  editStages,
  changePayment,
  addMessage,
  cancelLine,
  submitRequest,
  cancel,
  adjustMyRemittanceSchedule,
  uploadFileSubmit,
  somethingElse,
}

function hasLineStarted(line) {
  if (!line.stages) return true;
  const today = moment();
  const startOfLine = line.stages[0]?.start;
  const hasLineStarted = today > moment(startOfLine);
  return hasLineStarted;
}

export default function RequestLineChangeDialog({
  open = false,
  handleClose,
  currentStep,
}) {
  const history = useHistory();
  const snackbar = useSnackbarContext();
  const {
    lines,
    selectedLine,
    editLineCancel,
    editActiveLineClick,
    editActiveLineStep,
  } = useLinesContext();
  const [optionNumber, setOptionNumber] = useState(actions.None);
  const [state, setState] = useState(initialState);

  async function endRequest(
    deleteFiles: boolean = false,
    submittedRequest = false,
  ) {
    if (!submittedRequest) {
      handleClose();
      editActiveLineStep(steps.chooseOption);
    }
    const cloneLine = lines.find(
      (line) => line.origin_line === selectedLine.id,
    );
    if (state.LineDateChanged || state.LineDetailsChanged) {
      editLineCancel(cloneLine.id);
    }
    if (deleteFiles && state.files) await deleteAllFiles(state.files);
    setState(initialState);
  }

  function closeDialog() {
    handleClose();
    editActiveLineStep(steps.chooseOption);
    setState(initialState);
  }

  useEffect(() => {
    switch (optionNumber) {
      case actions.editStages:
        handleClose();
        editActiveLineClick(false, true, true);
        setState({ ...state, LineDateChanged: true });
        break;

      case actions.changePayment:
        handleClose();
        history.push(pathname.remittance.url);
        endRequest();
        break;
      case actions.addMessage:
        setState({ ...state, messageOnly: true });
        editActiveLineStep(steps.addMessage);
        break;
      case actions.cancelLine:
        const lineInProgress = hasLineStarted(selectedLine);
        if (lineInProgress) {
          snackbar.show(messages.lines.changeRequestLineInProgress);
        } else {
          setState({ ...state, cancelLine: true });
          editActiveLineStep(steps.cancelLine);
        }
        break;
      case actions.cancel:
        endRequest();
        break;
      case actions.uploadFileSubmit:
        setState({
          ...state,
          adjustMyRemittanceScheduleOnly: true,
        });
        editActiveLineStep(steps.addMessage);
        break;
      default:
        break;
    }
    setOptionNumber(actions.None);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionNumber]);

  useEffect(() => {
    if (currentStep === steps.cancel) {
      endRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  return (
    <div>
      {currentStep === steps.chooseOption && (
        <TypeOfChanges
          setStepNumber={editActiveLineStep}
          changedRequestInfo={state}
          setOptionNumber={setOptionNumber}
          open={open}
          endRequest={endRequest}
        />
      )}
      {currentStep === steps.editStages && (
        <EditStagesConfirmation
          setOptionNumber={setOptionNumber}
          open={open}
          endRequest={endRequest}
        />
      )}
      {currentStep === steps.changePayment && (
        <PaymentRedirect
          setOptionNumber={setOptionNumber}
          open={open}
          endRequest={endRequest}
        />
      )}
      {currentStep === steps.addMessage && (
        <AddMessage
          changes={state}
          setChangedRequestInfo={setState}
          setStepNumber={editActiveLineStep}
          open={open}
          endRequest={endRequest}
        />
      )}
      {currentStep === steps.cancelLine && (
        <CancelLineConfirmation
          setOptionNumber={setOptionNumber}
          changes={state}
          setChangedRequestInfo={setState}
          open={open}
          endRequest={endRequest}
        />
      )}
      {currentStep === steps.submitRequest && (
        <SubmitRequest
          onCancel={closeDialog}
          setOptionNumber={setOptionNumber}
          open={open}
        />
      )}
      {currentStep === steps.adjustMyRemittanceSchedule && (
        <AdjustMyRemittanceSchedule
          setOptionNumber={setOptionNumber}
          open={open}
          endRequest={endRequest}
        />
      )}
      {currentStep === steps.somethingElse && (
        <SomethingElse
          setOptionNumber={setOptionNumber}
          open={open}
          endRequest={endRequest}
        />
      )}
    </div>
  );
}
